<template>
	<div class="mac">
		<!-- /////////////////////// -->
		<!-- OVERLAY -->
		<div @click="onClose" class="mac__overlay"></div>

		<!-- /////////////////////// -->
		<!-- MODAL -->
		<div class="mac__modal">
			<!-- ~~~~~~~~~~~~~~~~~~ -->
			<!-- Close icon -->
			<div class="mac__modal-close">
				<IconClose @click="onClose" class="mac__modal-close-icon" />
			</div>

			<!-- ~~~~~~~~~~~~~~~~~~ -->
			<!-- Title -->
			<div class="mac__modal-title">Активация каналов</div>

			<!-- ~~~~~~~~~~~~~~~~~~ -->
			<!-- Каналов доступно -->
			<div class="mac__modal-channels">
				<div class="mac__modal-channels-label1">Каналов доступно</div>
				<div class="mac__modal-channels-label2">100 из 100</div>
			</div>

			<!-- ~~~~~~~~~~~~~~~~~~ -->
			<!-- Количество каналов для активации -->
			<div class="mac__modal-wrap">
				<div class="mac__modal-wrap-label">
					Количество каналов для активации
				</div>

				<!-- Выпадающий список с выбором числа каналов -->
				<UiNumberSelect
					class="mac__modal-wrap-select"
					:default-value="DEFAULT_CHANNELS"
					:total="50"
					v-on:selected-value="onSelectedValue"
					:is-disable="isDisable"
				/>

				<!-- Кнопка -->
				<UiButton
					v-if="!isDisable"
					@click="onClickCreateInstance"
					class="mac__modal-wrap-button"
					label="Активировать"
					background-color="var(--theme-color)"
					border-color="var(--theme-color)"
					color="rgb(255, 255, 255)"
					font-weight="600"
					font-size="16px"
				/>

				<UiButton
					v-if="isDisable"
					:disable="isDisable"
					class="mac__modal-wrap-button"
					label="Активировать"
					background-color="#c5c5c5"
					border-color="#c5c5c5"
					color="rgb(255, 255, 255)"
					font-weight="600"
					font-size="16px"
				/>

				<!-- Отмена -->
				<div @click="onClose" class="mac__modal-wrap-cancel">
					Отмена
				</div>

				<!-- Сообщение об ошибке -->
				<div v-if="isError" class="mac__modal-wrap-error">
					Ошибка: {{ errorMessage }}
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component";
import { mapActions, mapGetters } from "vuex";
import { IModal } from "@/interfaces/IModal";
import IconClose from "@/components/icons/IconClose.vue";

import UiButton from "@/components/ui/UiButton.vue";
import UiNumberSelect from "@/components/ui/UiNumberSelect.vue";
import { IInstanceCreate } from "@/interfaces/IInstanceCreate";

@Options({
	components: { UiNumberSelect, UiButton, IconClose },

	computed: {
		...mapGetters("instanceModule", {
			createdInstance: "instanceCreateGetters",
		}),
	},

	methods: {
		...mapActions("instanceModule", [
			"modalActivateChannelAction",
			"createInstanceAction",
			"instancesAction",
		]),
	},
})
export default class ModalActivateChannel extends Vue {
	public createdInstance: IInstanceCreate;

	// Константы
	public DEFAULT_CHANNELS = 1;
	public TIMEOUT = 3000;

	// Количество каналов для активации
	public numSelectedChannels = 1;

	// Query params
	public currentPage = 1;

	// ERRORS
	public isError = false;
	public errorMessage = "";

	public count = 0;
	public timeId = 0;
	public isDisable = false;

	// ************************************************************
	// Define mapActions
	public modalActivateChannelAction!: (payload: IModal) => void;
	public createInstanceAction!: () => void;
	public instancesAction!: (payload: {
		page?: number;
		partnerId?: number;
		instanceId?: string;
		token?: string;
		phone?: string;
		statusId?: string;
	}) => void;

	/**
	 * Hook mounted
	 */
	public mounted() {
		this.isError = false;
		this.errorMessage = "";
		this.count = 0;
		this.isDisable = false;

		const page = this.$route.query.page;
		this.currentPage = page !== undefined ? Number(page) : 1;

		document.body.style.overflowY = "hidden";
	}

	/**
	 * Hook unmounted
	 */
	public unmounted() {
		clearInterval(this.timeId);
	}

	/**
	 * Закрываем модалку
	 */
	public onClose() {
		document.body.style.overflowY = "auto";
		this.modalActivateChannelAction({ isShow: false });
	}

	/**
	 * Обработка нажатия кнопки - Активировать
	 */
	public async onClickCreateInstance() {
		this.isDisable = true;

		this.timeId = setInterval(async () => {
			this.count += 1;

			try {
				// Запрос на создание инстанса
				await this.createInstanceAction();

				// ERROR
				if (this.createdInstance.result === 0) {
					this.isError = true;
					this.errorMessage = this.createdInstance.error;
					this.isDisable = false;
					clearInterval(this.timeId); // Останавливаем цикл
				}
			} catch (e) {
				this.isDisable = false;
				clearInterval(this.timeId); // Останавливаем цикл
			}

			// Закрываем модалку, когда создано указанное количество каналов
			if (this.count >= this.numSelectedChannels) {
				await this.instancesAction({ page: this.currentPage });
				this.onClose();
			}
		}, this.TIMEOUT);
	}

	/**
	 * Выбор количества каналов для активации
	 * @param value
	 */
	public onSelectedValue(value: number) {
		this.numSelectedChannels = value;
	}
}
</script>

<style scoped lang="scss">
.mac {
	&__overlay {
		display: block;
		position: fixed;
		z-index: 1;
		left: 0;
		top: 0;
		width: 100%;
		height: 100%;
		overflow: auto;
		background-color: rgba(0, 0, 0, 0.4);
	}

	&__modal {
		display: block;
		position: fixed;
		z-index: 2;
		left: 50%;
		top: 50%;
		transform: translate(-50%, -50%);

		background-color: rgb(255, 255, 255);
		border: 1px solid rgb(255, 255, 255);
		border-radius: 12px;
		min-width: 292px;
		min-height: 361px;
	}

	&__modal-close {
		display: flex;
		justify-content: flex-end;
		padding: 16px 16px 0;
	}

	&__modal-close-icon {
		cursor: pointer;
	}

	&__modal-title {
		display: flex;
		justify-content: center;
		color: rgb(49, 49, 50);
		font-size: 18px;
		font-weight: 600;
	}

	&__modal-channels {
		display: flex;
		flex-direction: column;
		align-items: center;
		margin: 0 24px;
		border-bottom: 1px solid rgb(223, 230, 238);
	}

	&__modal-channels-label1 {
		padding: 16px 10px 10px;
		color: rgb(119, 121, 124);
		font-size: 14px;
	}

	&__modal-channels-label2 {
		padding: 4px 14px 14px;
		color: rgb(49, 49, 50);
		font-size: 24px;
		font-weight: 600;
	}

	&__modal-wrap {
		display: flex;
		flex-direction: column;
		padding: 24px;
	}

	&__modal-wrap-label {
		display: flex;
		justify-content: center;
		color: rgb(49, 49, 50);
		font-size: 14px;
	}

	&__modal-wrap-select {
		padding: 8px 0 0;
	}

	&__modal-wrap-button {
		padding-top: 16px;
	}

	&__modal-wrap-cancel {
		display: flex;
		justify-content: center;
		padding-top: 18px;
		color: rgb(74, 140, 240);
		cursor: pointer;
	}

	&__modal-wrap-error {
		display: flex;
		justify-content: center;
		padding-top: 18px;
		color: rgb(232, 57, 65);
		cursor: pointer;
	}

	@media screen and (max-width: 768px) {
		&__modal {
			width: 90%;
		}
	}
}
</style>
